import {
  coreExtensionData,
  createFrontendModule,
} from '@backstage/frontend-plugin-api';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import { PlatformAdminOnly } from './PlatformAdminOnly';

/**
 * Puts the catalog index behind the platform-admin permission.
 *
 * The table itself is deliberately left alone. The mockup draws `/catalog` as
 * the upstream page, unchanged, and marks nothing on it as proposed - the only
 * thing it asks for is that the page stop being part of everyone's product.
 * `../nav/Sidebar.tsx` already stopped advertising it, but hiding a link is not
 * access control: the route stayed mounted, so an old bookmark, a deep link out
 * of search or a typed URL still handed a tenant the operator surface. This
 * closes that, and it closes it in the frontend only - the catalog API has
 * always been filtered per user by the RBAC permission policy
 * (`packages/backend/src/modules/permissionPolicy.ts`), so what leaked was the
 * page, never another tenant's rows.
 *
 * `platformAdminViewPermission` is granted by no `RbacPolicy`, so the single
 * way through is the `rbac.platformAdmins` bypass - the same check that decides
 * whether the sidebar entry exists, which is what keeps the two from drifting.
 *
 * Only `page:catalog` is wrapped. Entity pages (`page:catalog/entity`) stay
 * open because they are where My stacks sends every tenant.
 */
const gatedCatalogPage = catalogPlugin.getExtension('page:catalog').override({
  factory(originalFactory) {
    const original = originalFactory();
    return [
      ...original,
      // Emitted after the original output, which is deduplicated by data ref:
      // the element is replaced while the page's path, route ref and title
      // carry over, so every route that resolves through the catalog's own
      // route refs keeps resolving.
      coreExtensionData.reactElement(
        <PlatformAdminOnly>
          {original.get(coreExtensionData.reactElement)}
        </PlatformAdminOnly>,
      ),
    ];
  },
});

export const catalogViewModule = createFrontendModule({
  pluginId: 'catalog',
  extensions: [gatedCatalogPage],
});
